import { EventType } from "@/analytics/analytics-events";
import { ToggleOpacityPanelEventProperties } from "@/analytics/viewer-settings-events-properties";
import { TransparencySliders } from "@/components/common/transparency-sliders/transparency-sliders";
import { useCanChangeTool } from "@/hooks/use-can-change-tool";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  selectActiveSettingsMenu,
  selectActiveTool,
  selectToolVisibility,
} from "@/store/ui/ui-selectors";
import {
  activateTool,
  setActiveSettingsMenu,
  ToolName,
  ToolVisibility,
} from "@/store/ui/ui-slice";
import {
  FaroText,
  FaroTooltip,
  Toolbar as FUIToolbar,
  grey,
  ToolButton,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Box, Stack, ToggleButtonGroupProps } from "@mui/material";
import { Fragment, useMemo, useState, type JSX, type ReactNode } from "react";
import { useTransparencySettingsContext } from "../common/transparency-sliders/transparency-settings-context";

/** Possible content of the tool's tooltip. It can be either a text or a custom component. */
type TooltipContent = string | JSX.Element;

type ToolTooltipTextOptions = {
  /** Text or custom component, to show on hover when the button is visible */
  default: TooltipContent;

  /** Text or custom component, to show on hover when the button is disabled */
  disabled?: TooltipContent;

  /** Text or custom component, to show on hover when the tool is waiting for something */
  waiting?: TooltipContent;

  /** True, if the tooltip should be interactive */
  isInteractive?: boolean;
};

/** Definition of the representation of a tool within the Toolbar component */
type Tool = {
  /** Name of the corresponding tool */
  name: ToolName;

  /** Icon to show in the toolbar */
  icon: JSX.Element;

  /** Text to show, as a tooltip, on the tool's button */
  tooltip?: ToolTooltipTextOptions;

  /** An optional callback on click of the tool */
  onClick?(): void;

  /** An optional element that will be shown on the left of button of the active tool */
  subMenu?: JSX.Element;

  /** Optional callback that will set the anchor for the button */
  setRefButton?(el: HTMLButtonElement): void;
};

export enum ToolStoreField {
  activeTool = "activeTool",
  activeSettingsMenu = "activeSettingsMenu",
  none = "none",
}

export type ToolGroup = {
  /** A unique key for the tool group */
  key: string;

  /** Props that can be passed to toggle button group */
  groupProps?: ToggleButtonGroupProps;

  /** List of Tools which should be in the given tool group */
  tools: Tool[];

  /** Which store field should the tool group dispatch its active tool */
  storeField: ToolStoreField;
};

interface Props {
  /** Groups of tools to be rendered */
  toolGroups: ToolGroup[];
  /**
   * true if the toolbar should allow overflow (e.g. if a badge on a button may overflow the width of the toolbar)
   * Otherwise, the toolbar will hide overflow.
   */
  allowOverflow?: boolean;

  /**
   * Optional callback that will be called with the HTMLDivElement of the toolbar once instantiated.
   *
   * @param el reference to the toolbar (its Stack element indeed)
   */
  setToolbarElement?(el: HTMLDivElement): void;
}

/**
 * @returns the internal toolbar used by CanvasToolbar to access to tools (Measure, Analysis, Annotation...) in the app
 */
export function ToolsToolbar({
  toolGroups,
  allowOverflow,
  setToolbarElement,
}: Props): JSX.Element | null {
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector(selectActiveTool);
  const toolsVisibility = useAppSelector(selectToolVisibility);

  const activeSettingsMenu = useAppSelector(selectActiveSettingsMenu);

  const isSettingsMenuOpen = activeSettingsMenu !== null;

  const objectsOpacity = useTransparencySettingsContext(
    (state) => state.objectsOpacity,
  );

  /** Filtering out the tools which are not visible */
  const filteredToolGroups = useMemo(
    () =>
      toolGroups
        .map((group) => ({
          ...group,
          tools: group.tools.filter(
            (tool) => toolsVisibility[tool.name] !== ToolVisibility.hidden,
          ),
        }))
        .filter((group) => group.tools.length > 0),
    [toolGroups, toolsVisibility],
  );

  // Get the sub tools for the current active tool
  const activeSubTools = useMemo(() => {
    for (const groups of filteredToolGroups) {
      for (const tool of groups.tools) {
        if (tool.name === activeTool) {
          return tool.subMenu;
        }
      }
    }
  }, [activeTool, filteredToolGroups]);

  // Compute the top position for the sub tool
  const { top, setActiveButton, setStack } = useComputeTopPosition();

  const canChangeTool = useCanChangeTool();
  const customTooltip = useMemo<string | undefined>(() => {
    if (activeTool === ToolName.createPDFReport) {
      return "Exit Create Report mode to use this feature.";
    }
  }, [activeTool]);

  return (
    <Box
      component="div"
      sx={{
        backdropFilter: "blur(4px) brightness(40%)",
        borderRadius: 1.5,
      }}
    >
      <Stack
        direction="row"
        ref={(el: HTMLDivElement) => {
          setStack(el);
          if (setToolbarElement) setToolbarElement(el);
        }}
      >
        {activeSettingsMenu === ToolName.opacity && <TransparencySliders />}
        <div
          style={{
            position: "absolute",
            top,
            // First translate by ratio -100% so the sub tool panel will be on the left of the tool button,
            // then translate by fixed -12px to have a consistent gap regardless the tool's size.
            transform: "translate(-100%, 0%) translate(-12px, 0px)",
          }}
        >
          {activeSubTools}
        </div>
        <FUIToolbar
          sx={{
            backdropFilter: "none",
            borderLeft: isSettingsMenuOpen
              ? ({ palette }) => `1px solid ${palette.white}66`
              : null,
          }}
          allowOverflow={allowOverflow}
        >
          {filteredToolGroups.map(({ key, groupProps, tools, storeField }) => (
            <Fragment key={key}>
              <ToolGroup
                {...groupProps}
                value={storeField2ToolGroupValue(
                  storeField,
                  activeTool,
                  activeSettingsMenu,
                )}
                exclusive={storeField !== ToolStoreField.none}
                onChange={(_, value: ToolName) => {
                  const isDisabled =
                    toolsVisibility[value] === ToolVisibility.disabled ||
                    toolsVisibility[value] === ToolVisibility.waiting ||
                    !canChangeTool;

                  if (isDisabled) return;

                  if (storeField === ToolStoreField.activeTool) {
                    trackToolActivation(value);

                    dispatch(activateTool(value));
                  } else if (storeField === ToolStoreField.activeSettingsMenu) {
                    if (
                      value === ToolName.opacity ||
                      activeSettingsMenu === ToolName.opacity
                    ) {
                      Analytics.track<ToggleOpacityPanelEventProperties>(
                        EventType.toggleOpacityPanel,
                        {
                          isOpened: value === ToolName.opacity,
                          currentCadOpacity: objectsOpacity.cadOpacity,
                          currentPointCloudOpacity: objectsOpacity.pcOpacity,
                          currentMapOpacity: objectsOpacity.mapOpacity,
                        },
                      );
                    }

                    dispatch(setActiveSettingsMenu(value));
                  }
                }}
              >
                {tools.map(({ name, icon, tooltip, onClick, setRefButton }) => {
                  if (toolsVisibility[name] === ToolVisibility.hidden) {
                    return null;
                  }

                  const isDisabled =
                    toolsVisibility[name] === ToolVisibility.disabled ||
                    toolsVisibility[name] === ToolVisibility.waiting ||
                    !canChangeTool;

                  // Create the tool's button component.
                  const toolButton = (
                    <ToolButton
                      showSpinner={
                        toolsVisibility[name] === ToolVisibility.waiting
                      }
                      key={name}
                      value={name}
                      aria-label={name}
                      selected={
                        (activeTool === name || activeSettingsMenu === name) &&
                        !isDisabled
                      }
                      ref={(el: HTMLButtonElement) => {
                        if (activeTool === name) {
                          setActiveButton(el);
                        } else if (setRefButton) {
                          setRefButton(el);
                        }
                      }}
                      disabled={isDisabled}
                      sx={{
                        ...(activeSettingsMenu === name && {
                          borderBottomLeftRadius: 0,
                          borderTopLeftRadius: 0,
                        }),
                      }}
                      onClick={onClick}
                    >
                      {icon}
                    </ToolButton>
                  );

                  // If the tool has a tooltip, then wrap the button with the Tooltip component.
                  return tooltip ? (
                    <FaroTooltip
                      key={name}
                      title={findBestTooltipContent(
                        tooltip,
                        toolsVisibility[name],
                        canChangeTool ? undefined : customTooltip,
                      )}
                      placement={
                        activeSubTools && name === activeTool
                          ? "bottom"
                          : "left"
                      }
                      disableInteractive={!tooltip.isInteractive}
                    >
                      {toolButton}
                    </FaroTooltip>
                  ) : (
                    toolButton
                  );
                })}
              </ToolGroup>
            </Fragment>
          ))}
        </FUIToolbar>
      </Stack>
    </Box>
  );
}

/**
 * @param tooltip object containing all the available tooltip content for the tool
 * @param visibility the visibility status of the tool
 * @param customTooltip A custom tooltip used to override the tool default ones
 * @returns the tooltip content based on the state of the button and the available tooltips.
 */
function findBestTooltipContent(
  tooltip: ToolTooltipTextOptions,
  visibility: ToolVisibility,
  customTooltip: string | undefined,
): ReactNode {
  let tooltipContent = tooltip.default;

  if (customTooltip) {
    tooltipContent = customTooltip;
  }
  // If the button is disabled and the tooltip object has a specific tooltip for the disabled button
  // then use the specific tooltip.
  else if (
    visibility === ToolVisibility.disabled &&
    tooltip.disabled !== undefined
  ) {
    tooltipContent = tooltip.disabled;
  } else if (
    visibility === ToolVisibility.waiting &&
    tooltip.waiting !== undefined
  ) {
    tooltipContent = tooltip.waiting;
  }

  return tooltipContent;
}

type ToolsTooltipTextProps = {
  /** The text to display in the tooltip */
  text: string;
};

/**
 * @returns a text component with predefined style for the tooltip
 */
export function ToolsTooltipText({ text }: ToolsTooltipTextProps): JSX.Element {
  return (
    <FaroText variant="bodyM" sx={{ color: grey[100] }}>
      {text}
    </FaroText>
  );
}

/**
 * Tracking the activation of the individual tools
 *
 * @param toolName The name of the tool that has been activated
 */
function trackToolActivation(toolName: ToolName): void {
  switch (toolName) {
    case ToolName.annotation:
      Analytics.track(EventType.triggerAnnotationCreationTool);
  }
}

type ComputeTopPositionReturn = {
  /** The top position of the sub tool panel */
  top: number | undefined;
  /** Callback to set the reference to the active button */
  setActiveButton(el: HTMLButtonElement): void;
  /** Callback to set the reference to the toolbar */
  setStack(el: HTMLDivElement): void;
};

/**
 * This hook computes the current top screen coordinate of the active button within the toolbar.
 * This coordinate can be used to position sub-toolbars or submenus that are spawned by the selected tool.
 *
 * @returns The current top coordinate
 */
function useComputeTopPosition(): ComputeTopPositionReturn {
  // This state contains the reference to the active button html element
  const [activeButton, setActiveButton] = useState<HTMLButtonElement>();
  // This state contains the reference to the toolbar html element
  const [stack, setStack] = useState<HTMLDivElement>();
  const top = useMemo(() => {
    if (!activeButton || !stack) return;
    return (
      activeButton.getBoundingClientRect().top -
      stack.getBoundingClientRect().top
    );
  }, [activeButton, stack]);

  return {
    top,
    setActiveButton,
    setStack,
  };
}

function storeField2ToolGroupValue(
  storeField: ToolStoreField,
  activeTool: ToolName | null,
  activeSettingsMenu: ToolName | null,
): ToolName | null {
  switch (storeField) {
    case ToolStoreField.activeTool:
      return activeTool;
    case ToolStoreField.activeSettingsMenu:
      return activeSettingsMenu;
    case ToolStoreField.none:
      return null;
  }
}
